/**
 * Slide-editor-specific block type dropdown. Mirrors BlockOptionsDropdown in
 * compact mode but with slide-editor-specific positioning and highlight styling.
 * Kept local to avoid changing the shared BlockOptionsDropdown component.
 */
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import { $createHeadingNode, $createQuoteNode } from '@lexical/rich-text';
import { $setBlocksType } from '@lexical/selection';
import { $findMatchingParent } from '@lexical/utils';
import { Box, Stack, Typography } from '@mui/material';
import {
  $createParagraphNode,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isRootOrShadowRoot,
  LexicalEditor,
  LexicalNode,
} from 'lexical';
import { useCallback, useMemo } from 'react';
import { usePreviousDistinct } from 'react-use';

import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { $isFontSizeNode } from '@/components/TextEditor/plugins/FontSizePlugin/FontSizeNode';
import {
  Block,
  blockTypeToBlockIcon,
  blockTypeToBlockName,
  iconProps,
} from '@/components/TextEditor/plugins/ToolbarPlugin/ToolbarPlugin.utils';
import { zIndexes } from '@/styles/zIndexes';

interface SlideEditorBlockDropdownProps {
  editor: LexicalEditor;
  blockType: Block;
  isOpen?: boolean;
  onClick?: () => void;
  onClose?: () => void;
}

function BlockDropdownItem({
  icon: Icon,
  label,
  isActive,
}: {
  icon: React.ComponentType<{ size?: number }>;
  label: string;
  isActive: boolean;
}) {
  return (
    <Stack direction="row" spacing={1} flex={1} width="100%">
      <Box display="flex">
        <Icon {...iconProps} />
      </Box>
      <Typography variant="body-s" sx={{ fontWeight: isActive ? 700 : 400 }}>
        {label}
      </Typography>
    </Stack>
  );
}

/**
 * Recursively find and unwrap all FontSizeNodes within a subtree.
 */
function $unwrapFontSizeNodesInSubtree(node: LexicalNode): void {
  if ($isFontSizeNode(node)) {
    const children = node.getChildren();
    for (const child of children) {
      node.insertBefore(child);
    }
    node.remove();
    // Children were moved before the (now removed) node; process them
    // in case they contain nested FontSizeNodes.
    for (const child of children) {
      $unwrapFontSizeNodesInSubtree(child);
    }
    return;
  }
  if ($isElementNode(node)) {
    // Iterate a snapshot since unwrapping mutates the child list
    for (const child of [...node.getChildren()]) {
      $unwrapFontSizeNodesInSubtree(child);
    }
  }
}

/**
 * Remove all FontSizeNode wrappers within the current selection's blocks.
 * Heading styles control their own size, so inline size overrides should
 * be stripped when switching to a heading block type. Strips the entire
 * block — not just nodes under the cursor — so that a collapsed selection
 * doesn't leave FontSizeNodes elsewhere in the paragraph.
 */
function $stripFontSizeNodes(): void {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return;

  const blocks = new Set<LexicalNode>();
  // Collect blocks before unwrapping, which detaches nodes in the selection.
  for (const node of selection.getNodes()) {
    // Find the top-level block element containing this node
    const block =
      $findMatchingParent(node, (n) => {
        const parent = n.getParent();
        return parent !== null && $isRootOrShadowRoot(parent);
      }) ?? node.getTopLevelElementOrThrow();

    blocks.add(block);
  }

  for (const block of blocks) {
    $unwrapFontSizeNodesInSubtree(block);
  }
}

export function SlideEditorBlockDropdown({
  editor,
  blockType,
  isOpen,
  onClick,
  onClose,
}: SlideEditorBlockDropdownProps) {
  const formatParagraph = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  }, [editor]);

  const formatLargeHeading = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $stripFontSizeNodes();
        // Re-read selection after stripping — node references may be stale
        const freshSelection = $getSelection();
        if ($isRangeSelection(freshSelection)) {
          $setBlocksType(freshSelection, () => $createHeadingNode('h1'));
        }
      }
    });
  }, [editor]);

  const formatMediumHeading = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $stripFontSizeNodes();
        // Re-read selection after stripping — node references may be stale
        const freshSelection = $getSelection();
        if ($isRangeSelection(freshSelection)) {
          $setBlocksType(freshSelection, () => $createHeadingNode('h2'));
        }
      }
    });
  }, [editor]);

  const formatSmallHeading = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $stripFontSizeNodes();
        // Re-read selection after stripping — node references may be stale
        const freshSelection = $getSelection();
        if ($isRangeSelection(freshSelection)) {
          $setBlocksType(freshSelection, () => $createHeadingNode('h3'));
        }
      }
    });
  }, [editor]);

  const formatBulletList = useCallback(() => {
    if (blockType !== 'ul') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  }, [blockType, editor]);

  const formatNumberedList = useCallback(() => {
    if (blockType !== 'ol') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  }, [blockType, editor]);

  const formatQuote = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createQuoteNode());
      }
    });
  }, [editor]);

  const prevBlockType = usePreviousDistinct(blockType);

  const label = useMemo(
    () =>
      blockTypeToBlockName[blockType] ??
      (prevBlockType ? blockTypeToBlockName[prevBlockType] : 'Body'),
    [blockType, prevBlockType]
  );

  const items = useMemo(
    () => [
      {
        type: 'h1' as const,
        Icon: blockTypeToBlockIcon.h1,
        label: blockTypeToBlockName.h1,
        onClick: formatLargeHeading,
      },
      {
        type: 'h2' as const,
        Icon: blockTypeToBlockIcon.h2,
        label: blockTypeToBlockName.h2,
        onClick: formatMediumHeading,
      },
      {
        type: 'h3' as const,
        Icon: blockTypeToBlockIcon.h3,
        label: blockTypeToBlockName.h3,
        onClick: formatSmallHeading,
      },
      {
        type: 'paragraph' as const,
        Icon: blockTypeToBlockIcon.paragraph,
        label: blockTypeToBlockName.paragraph,
        onClick: formatParagraph,
      },
      {
        type: 'ul' as const,
        Icon: blockTypeToBlockIcon.ul,
        label: blockTypeToBlockName.ul,
        onClick: formatBulletList,
      },
      {
        type: 'ol' as const,
        Icon: blockTypeToBlockIcon.ol,
        label: blockTypeToBlockName.ol,
        onClick: formatNumberedList,
      },
      {
        type: 'quote' as const,
        Icon: blockTypeToBlockIcon.quote,
        label: blockTypeToBlockName.quote,
        onClick: formatQuote,
      },
    ],
    [
      formatLargeHeading,
      formatMediumHeading,
      formatSmallHeading,
      formatParagraph,
      formatBulletList,
      formatNumberedList,
      formatQuote,
    ]
  );

  return (
    <ButtonWithPopover
      size="xs"
      variant="transparent"
      popperVariant="menuBelow"
      highlightWhenOpen
      isOpen={isOpen}
      onClick={onClick}
      onClose={onClose}
      popperProps={{
        anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
        transformOrigin: { horizontal: 'left', vertical: 'top' },
        // 9px = 5px (toolbar padding-bottom 4px + border 1px) + 4px desired gap,
        // matching the prompt dropdown's visual alignment below the toolbar border.
        PaperProps: { sx: { mt: '9px' } },
        sx: { zIndex: zIndexes.TOOLTIP },
        disablePortal: false,
      }}
      label={
        <Typography
          variant="body-xs"
          overflow="hidden"
          whiteSpace="nowrap"
          textOverflow="ellipsis"
          // Inherit the button's text color (primary) instead of the
          // body-xs variant's default secondary color.
          sx={{ color: 'inherit' }}
        >
          {label}
        </Typography>
      }
    >
      {items.map(({ type, Icon, label: itemLabel, onClick }) => (
        <MenuItem
          key={type}
          justifyContent="space-between"
          label={
            <BlockDropdownItem
              icon={Icon}
              label={itemLabel}
              isActive={type === blockType}
            />
          }
          onClick={onClick}
          sx={{ width: '240px' }}
        />
      ))}
    </ButtonWithPopover>
  );
}
